import { BulkCapabilityResponse } from 'types/capabilities/capabilities'
import { EnableFiatCurrencyFilter } from 'types/types'

export interface WalletProperties {
  currency: string
  supportsWithdrawals?: boolean
  supportsThirdParty?: boolean
  supportsDeposits?: boolean
  lsid: string
  isLegacyWallet?: boolean
}

interface FiatWalletCapabilities {
  isDepositEnabled?: boolean
  isWithdrawEnabled?: boolean
  isPayEnabled?: boolean
  isTransferEnabled?: boolean
}

interface CryptoWalletCapabilities {
  isSendEnabled: boolean
  isReceiveEnabled: boolean
  isTransferEnabled: boolean
}

interface FiatWalletActions {
  supportsPay: boolean
  supportsDeposits: boolean
  supportsWithdrawals: boolean
  supportsInternalTransfers: boolean
}

interface CryptoWalletActions {
  supportsSend: boolean
  supportsReceive: boolean
  supportsInternalTransfers: boolean
}

interface GetFiatWalletActionSupportProps {
  walletProperties?: WalletProperties
  bulkCapabilities: BulkCapabilityResponse
  enableFiatCurrencyFilter?: EnableFiatCurrencyFilter
  enableFiatPayinCapabilityV2?: boolean
  enableFiatPayoutCapabilityV2?: boolean
}

const walletActionsSupport = (
  walletProperties: WalletProperties,
  enableFiatCurrencyFilter: EnableFiatCurrencyFilter
) => {
  const { currency, supportsWithdrawals, supportsThirdParty, supportsDeposits } = walletProperties
  const supportedWithdrawCurrencies = enableFiatCurrencyFilter?.supportedWithdrawCurrencies || []
  const supportedDepositCurrencies = enableFiatCurrencyFilter?.supportedDepositCurrencies || []
  const supportedPayCurrencies = enableFiatCurrencyFilter?.supportedPayCurrencies || []
  const walletSupportsWithdrawals =
    supportsWithdrawals && supportedWithdrawCurrencies.includes(currency?.toUpperCase())
  const walletSupportsPay =
    supportsThirdParty && supportedPayCurrencies?.includes(currency?.toUpperCase())
  const walletSupportsDeposits =
    (supportedDepositCurrencies?.includes(currency?.toUpperCase()) && supportsDeposits) ||
    (!supportedDepositCurrencies && supportsDeposits)

  return {
    supportsPay: walletSupportsPay,
    supportsDeposits: walletSupportsDeposits,
    supportsWithdrawals: walletSupportsWithdrawals,
  }
}

/**
 * Returns wallet actions capability support for a wallet
 *
 * @param {WalletProperties} walletProperties - Some basic wallet properties used to determine wallet action capabilities
 * @param {BulkCapabilityResponse} bulkCapabilities - Capabilities from the capabilities management service used to determine wallet action capabilities
 * @returns {FiatWalletActions} - Returns the actions (deposit, pay, withdraw and internal transfers),
 * that a given wallet is enabled for.
 *
 * - If No bulk capabilities are recieved, defalt to existing logic.
 *   This logic will be removed once all processing profile capabilities are created
 *   processing profile capabilities are created
 */
export const getFiatWalletActionSupport = ({
  walletProperties,
  bulkCapabilities,
  enableFiatCurrencyFilter,
  enableFiatPayinCapabilityV2,
  enableFiatPayoutCapabilityV2,
}: GetFiatWalletActionSupportProps): FiatWalletActions => {
  if (bulkCapabilities) {
    const { isDepositEnabled, isPayEnabled, isWithdrawEnabled, isTransferEnabled } =
      getFiatWalletCapabilities(
        walletProperties?.lsid,
        bulkCapabilities,
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2
      )

    const isActionCapabilityDefined =
      isDepositEnabled !== undefined &&
      isPayEnabled !== undefined &&
      isWithdrawEnabled !== undefined
    const isTransferActionCapabilityDefined = isTransferEnabled !== undefined

    if (walletProperties?.isLegacyWallet) {
      const { supportsPay, supportsDeposits, supportsWithdrawals } = walletActionsSupport(
        walletProperties,
        enableFiatCurrencyFilter
      )

      return {
        supportsPay,
        supportsDeposits,
        supportsWithdrawals,
        supportsInternalTransfers: isTransferActionCapabilityDefined && isTransferEnabled,
      }
    }

    return {
      supportsPay: isActionCapabilityDefined && isPayEnabled,
      supportsDeposits: isActionCapabilityDefined && isDepositEnabled,
      supportsWithdrawals: isActionCapabilityDefined && isWithdrawEnabled,
      supportsInternalTransfers: isTransferActionCapabilityDefined && isTransferEnabled,
    }
  }

  return {
    supportsPay: false,
    supportsDeposits: false,
    supportsWithdrawals: false,
    supportsInternalTransfers: false,
  }
}

/**
 * Returns wallet actions capability support for a wallet
 *
 * @param {WalletProperties} walletProperties - Some basic wallet properties used to determine wallet action capabilities
 * @param {BulkCapabilityResponse} bulkCapabilities - Capabilities from the capabilities management service used to determine wallet action capabilities
 * @returns {CryptoWalletActions} - Returns the actions (send, receive and internal transfers),
 * that a given wallet is enabled for.
 */
export const getCryptoWalletActionSupport = ({
  bulkCapabilities,
}: GetFiatWalletActionSupportProps): CryptoWalletActions => {
  if (bulkCapabilities) {
    const { isSendEnabled, isReceiveEnabled, isTransferEnabled } = getCryptoWalletCapabilities()

    const isActionCapabilityDefined =
      isSendEnabled !== undefined &&
      isReceiveEnabled !== undefined &&
      isTransferEnabled !== undefined
    const isTransferActionCapabilityDefined = isTransferEnabled !== undefined

    return {
      supportsSend: isActionCapabilityDefined && isSendEnabled,
      supportsReceive: isActionCapabilityDefined && isReceiveEnabled,
      supportsInternalTransfers: isTransferActionCapabilityDefined && isTransferEnabled,
    }
  }

  return {
    supportsSend: true,
    supportsReceive: true,
    supportsInternalTransfers: false,
  }
}

/**
 * Returns wallet action capabilities from the capabilities management service
 *
 * @param {string} walletLsid - The wallet to fetch wallet action capabilitys for
 * @param {BulkCapabilityResponse} bulkCapabilities - The bulk wallet capabilities response.
 * @returns {FiatWalletCapabilities} - Returns the actions (deposit, pay, withdraw and internal transfers),
 * that a given wallet is enabled for.
 */
export const getFiatWalletCapabilities = (
  walletLsid: string,
  bulkCapabilities: BulkCapabilityResponse,
  enableFiatPayinCapabilityV2: boolean,
  enableFiatPayoutCapabilityV2: boolean
): FiatWalletCapabilities => {
  // Payin and payout v1 capabilities
  const walletActionsCapabilityResponse =
    bulkCapabilities.FIAT?.WALLET_ACTIONS?.WALLET?.[walletLsid]
  const actions = walletActionsCapabilityResponse?.value?.toString()?.split(',')

  // Payin v2 capabilities
  const walletPayinCapabilityResponse = bulkCapabilities.FIAT?.PAYINS?.WALLET?.[walletLsid]
  const isPayinEnabled = walletPayinCapabilityResponse?.value === 'true'

  // Payout v2 capabilities
  const walletPayoutFirstPartyCapabilityResponse =
    bulkCapabilities.FIAT?.PAYOUTS_FIRST_PARTY?.WALLET?.[walletLsid]
  const isPayoutFirstPartyEnabled = walletPayoutFirstPartyCapabilityResponse?.value === 'true'

  const walletPayoutThirdPartyCapabilityResponse =
    bulkCapabilities.FIAT?.PAYOUTS_THIRD_PARTY?.WALLET?.[walletLsid]
  const isPayoutThirdPartyEnabled = walletPayoutThirdPartyCapabilityResponse?.value === 'true'

  const actionsValue = {
    ...(walletActionsCapabilityResponse &&
      !!walletActionsCapabilityResponse?.value &&
      !walletActionsCapabilityResponse?.error && {
        isPayEnabled: enableFiatPayoutCapabilityV2
          ? isPayoutThirdPartyEnabled
          : actions?.includes('pay'),
        isWithdrawEnabled: enableFiatPayoutCapabilityV2
          ? isPayoutFirstPartyEnabled
          : actions?.includes('withdraw'),
        isDepositEnabled: enableFiatPayinCapabilityV2
          ? isPayinEnabled
          : actions?.includes('deposit'),
      }),
  }

  return {
    ...actionsValue,
    isTransferEnabled: true,
  }
}

/**
 * Returns wallet action capabilities from the capabilities management service
 *
 * @param {string} walletLsid - The wallet to fetch wallet action capabilitys for
 * @param {BulkCapabilityResponse} bulkCapabilities - The bulk wallet capabilities response.
 * @returns {CryptoWalletCapabilities} - Returns the actions (send, receive and internal transfers),
 * that a given wallet is enabled for.
 */
export const getCryptoWalletCapabilities = (): CryptoWalletCapabilities => {
  return {
    isSendEnabled: true,
    isReceiveEnabled: true,
    isTransferEnabled: true,
  }
}
